import { checkPasswordStrength } from "capitalrx-utilities";
import validator from "validator";

import { VALIDATION } from "../../../constants/validation";

/**
 * The types of fields that can be validated.
 */
export type Validatable = string;

/**
 * The validation result. Either true/false or the message to display.
 */
export type ValidationResult = boolean | string;

/**
 * Returns a validator function which checks input against the given regex .
 * @param regex to check against.
 * @returns The validation function.
 */
export function matchesRegex(regex: string, message?: string): (value: Validatable) => ValidationResult {
  return (value: Validatable) => {
    const regX = new RegExp(regex);
    const match = regX.test(value);
    if (match) {
      return true;
    }
    return message ? message : "Invalid entry";
  };
}

/**
 * Validates the specified password string.
 * @param password The password to validate.
 * @returns The validation result.
 */
export function meetsPasswordRequirements(password: Validatable): ValidationResult {
  const { length, lowercase, number, specialChar, uppercase } = checkPasswordStrength(password);
  if (length && lowercase && number && specialChar && uppercase) return true;

  const errors: Array<string> = [];

  if (!length) {
    errors.push("at least 15 characters");
  }

  if (!lowercase) {
    errors.push("at least 1 lowercase letter");
  }

  if (!number) {
    errors.push("at least 1 number");
  }

  if (!specialChar) {
    errors.push("at least 1 special character");
  }

  if (!uppercase) {
    errors.push("at least 1 uppercase letter");
  }

  if (errors.length > 1) {
    errors[errors.length - 1] = "and " + errors[errors.length - 1];
  }

  const separator = errors.length === 2 ? " " : ", ";
  return `Password must contain ${errors.join(separator)}.`;
}

/**
 * Validates whether the two specified passwords match.
 * @param currentPassword The current password.
 * @param newPassword The new password.
 * @returns The validation result.
 */
export function passwordsMatch(currentPassword: Validatable, newPassword: Validatable): ValidationResult {
  if (currentPassword !== newPassword) {
    return "Your passwords don't match.";
  }
  return true;
}

/**
 * Validates whether the specified value is a date.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isDate(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isDate(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @param strict Defaults to false; if set to true, validator will reject inputs different from format.
 * @returns The validation result.
 */
export function isDate(value: Validatable, message: string, strict: boolean): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @param strict Defaults to false; if set to true, validator will reject inputs different from format.
 * @param format Defaults to MM/DD/YYYY
 * @returns The validation result.
 */
export function isDate(value: Validatable, message: string, strict: boolean, format: string): ValidationResult;

/**
 * Validates whether the specified value is a date.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @param strict Defaults to false; if set to true, validator will reject inputs different from format.
 * @returns The validation result.
 */
export function isDate(value: Validatable, message?: string, strict = false, format = "MM/DD/YYYY"): ValidationResult {
  if (value === undefined || validator.isDate(value, { format: format, strictMode: strict })) {
    return true;
  }

  return message ? message : "Invalid date";
}

/**
 * Validates whether the string is a date that is after a specified date.
 * @param value The value to validate.
 * @param comparisonDate Defaults to now.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isAfter(value: Validatable, comparisonDate: string, message: string): ValidationResult {
  if (value === undefined || validator.isAfter(value, comparisonDate)) {
    return true;
  }

  return message ? message : "Date is set too early";
}

/**
 * Validates whether the string is a date that is after a specified date.
 * @param value The value to validate.
 * @param comparisonDate Defaults to now.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isBefore(value: Validatable, comparisonDate: string, message: string): ValidationResult {
  if (value === undefined || validator.isBefore(value, comparisonDate)) {
    return true;
  }

  return message ? message : "Date must before current date and time";
}

/**
 * Validates whether the specified value is a time.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isTime(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isTime(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @param mode Defaults to "default", will validate HH:MM format; if set to "withSeconds", will validate the HH:MM:SS format.
 * @returns The validation result.
 */
export function isTime(value: Validatable, message: string, mode: "default" | "withSeconds" | undefined): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @param mode Defaults to "default", will validate HH:MM format; if set to "withSeconds", will validate the HH:MM:SS format.
 * @returns The validation result.
 */
export function isTime(value: Validatable, message: string, mode: "default" | "withSeconds" | undefined): ValidationResult;

/**
 * Validates whether the specified value is a date, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @param mode Defaults to "default", will validate HH:MM format; if set to "withSeconds", will validate the HH:MM:SS format.
 * @param hourFormat Defaults to "hour24", will validate the 24-hour format; if set to "hour12", will validate the 12-hour format.
 * @returns The validation result.
 */
export function isTime(
  value: Validatable,
  message?: string,
  mode: "default" | "withSeconds" | undefined = "default",
  hourFormat: "hour24" | "hour12" = "hour24",
): ValidationResult {
  if (value === undefined || validator.isTime(value, { hourFormat: hourFormat, mode: mode })) {
    return true;
  }

  return message ? message : "Invalid time";
}

/**
 * Validates whether the specified value is numeric.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isNumeric(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is numeric, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isNumeric(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is numeric.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isNumeric(value: Validatable, message?: string): ValidationResult {
  if (value === undefined || validator.isNumeric(value)) {
    return true;
  }

  return message ? message : "Not a Valid Number.";
}

export function isPositiveNumeric(value: number, message?: string): ValidationResult {
  if (value > 0) {
    return true;
  }

  return message ? message : "Number must be greater than 0.";
}

/**
 * Validates whether the specified value is an e-mail address.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isEmail(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is an e-mail address, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isEmail(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is an e-mail address.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isEmail(value: Validatable, message?: string): ValidationResult {
  if (value === undefined || validator.isEmail(value)) {
    return true;
  }

  return message ? message : "Invalid email address.";
}

/**
 * Validates whether the specified value is alphanumeric.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isAlphanumeric(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is alphanumeric, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isAlphanumeric(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is alphanumeric.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isAlphanumeric(value: Validatable, message?: string): ValidationResult {
  if (value === undefined || validator.isAlphanumeric(value)) {
    return true;
  }

  return message ? message : "Only alphanumeric characters.";
}

/**
 * Validates whether the specified value is JSON.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isJSON(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is JSON, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isJSON(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is JSON.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isJSON(value: Validatable, message?: string): ValidationResult {
  if (value === undefined || validator.isJSON(value)) {
    return true;
  }

  return message ? message : "Not a valid JSON structure";
}

/**
 * Validates whether the specified value is a valid name.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isName(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value is a valid name, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isName(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value is a valid name.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isName(value: Validatable, message?: string): ValidationResult {
  const regex = new RegExp(/^[a-z ,.'-]+$/i);
  const match = regex.test(value);
  if (match) {
    return true;
  }

  return message ? message : "Unexpected character in name.";
}

/**
 * Validates whether the specified value is a valid resource name.
 * This means it contains only letters, numbers or dashes
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isResourceName(value: Validatable, message?: string): ValidationResult {
  const regex = new RegExp(/^[a-zA-Z0-9àáâäãåąæčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð-]+$/);
  const match = regex.test(value);
  if (match) {
    return true;
  }

  return message ? message : "Only letters, numbers and dashes allowed.";
}

/**
 * Indicates that the specified value is required.
 */
export const required = {
  value: true,
  message: VALIDATION.REQUIRED,
};

export function isZipCode(value: Validatable, message?: string): ValidationResult {
  const regex = new RegExp(/^\d{5}(-\d{4})?$/);
  const match = regex.test(value);
  if (match) {
    return true;
  }

  return message ? message : "Not a valid zip code.";
}

export function isPhoneNumber(value: Validatable, message?: string): ValidationResult {
  const regex = new RegExp(/^(\d{3}|\(\d{3}\))[- ]?\d{3}[- ]?\d{4}?$/);
  const match = regex.test(value);
  if (match) {
    return true;
  }

  return message ? message : "Not a valid phone number.";
}

/**
 * Validates whether the specified value contains only ascii characters.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isAscii(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value contains only ascii characters, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isAscii(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value contains only ascii characters.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isAscii(value: Validatable, message?: string): ValidationResult {
  if (value === undefined || validator.isAscii(value)) {
    return true;
  }

  return message ? message : "Must only contain Ascii characters";
}

export function notRequired(
  validator: (value: Validatable, message?: string) => ValidationResult,
): (value: Validatable, message?: string) => ValidationResult {
  return (value, message) => {
    if (value === undefined || value === null || value.length === 0 || value === "") {
      return true;
    } else {
      return validator(value, message);
    }
  };
}

/**
 * Validates whether the specified date is before or equal to today's date.
 * @param date The date to validate.
 * @param todaysDate Current date.
 * @returns The validation result.
 */
export function dateIsNotInFuture(date: Validatable, todaysDate: Validatable): ValidationResult;

/**
 * Validates whether the specified date is before or equal to today's date, and returns the specified message if invalid.
 * @param date The date to validate.
 * @param todaysDate Current date.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function dateIsNotInFuture(date: Validatable, todaysDate: Validatable, message: Validatable): ValidationResult;

/**
 * Validates whether the specified date is before or equal to today's date.
 * @param date The date to validate.
 * @param todaysDate Current date.
 * @param message? The optional message to display if invalid.
 * @returns The validation result.
 */

export function dateIsNotInFuture(date: Validatable, todaysDate: Validatable, message?: string): ValidationResult {
  if (validator.isBefore(date, todaysDate) || validator.equals(date, todaysDate)) {
    return true;
  }

  return message ? message : "Invalid date.";
}

/**
 * Validates whether the specified end date is equal to or after the given start date.
 * @param startDate The given start date.
 * @param endDate The end date to validate.
 * @returns The validation result.
 */
export function endDateIsAfterStartDate(startDate: Validatable, endDate: Validatable): ValidationResult;

/**
 * * Validates whether the specified end date is equal to or after the given start date, and returns the specified message if invalid.
 * @param startDate The given start date.
 * @param endDate The end date to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function endDateIsAfterStartDate(startDate: Validatable, endDate: Validatable, message: Validatable): ValidationResult;

/**
 * * Validates whether the specified end date is equal to or after the given start date.
 * @param startDate The given start date.
 * @param endDate The end date to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */

export function endDateIsAfterStartDate(startDate: Validatable, endDate: Validatable, message?: string): ValidationResult {
  if (startDate === undefined) {
    return true;
  }

  // check for invalid startDate, eg 'asdf' or ''
  if (!validator.isDate(startDate, { format: "MM/DD/YYYY", strictMode: true })) {
    return true;
  }

  if (validator.isAfter(endDate, startDate) || validator.equals(endDate, startDate)) {
    return true;
  }

  return message ? message : "Invalid date.";
}

/**
 * Validates whether the specified value conforms to a base 64 cert format.
 * @param value The value to validate.
 * @returns The validation result.
 */
export function isCertificate(value: Validatable): ValidationResult;

/**
 * Validates whether the specified value conforms to a base 64 cert format, and returns the specified message if invalid.
 * @param value The value to validate.
 * @param message The message to display if invalid.
 * @returns The validation result.
 */
export function isCertificate(value: Validatable, message: string): ValidationResult;

/**
 * Validates whether the specified value conforms to a base 64 cert format.
 * @param value The value to validate.
 * @param message The optional message to display if invalid.
 * @returns The validation result.
 */
export function isCertificate(value: Validatable, message?: string): ValidationResult {
  const errorMessage = message ? message : "Must be a valid base 64 certificate";
  const valueStripped = value.replace(/[\n\r]+/g, "");
  const captureGroups = valueStripped.match(/(-----BEGIN CERTIFICATE-----)(.+)(-----END CERTIFICATE-----)/);
  const base64Group = captureGroups ? captureGroups[2] : null;
  if (base64Group) {
    return validator.isBase64(base64Group) ? true : errorMessage;
  }
  return errorMessage;
}
